import axios from "axios";
import { getActionTrail } from "./actionTrail";

const STATE_KEY = "tripbox:errorState";
const STATE_TTL_MS = 10 * 60 * 1000;

interface StoredErrorState {
  signature: string;
  id: string | null;
  reloaded: boolean;
  at: number;
}

/** Result of recording an error: the server log id and whether the user already reloaded once. */
export interface CapturedError {
  id: string | null;
  reloadedBefore: boolean;
}

const inFlight = new Map<string, Promise<CapturedError>>();

const readState = (): StoredErrorState | null => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(STATE_KEY) || "null") as StoredErrorState | null;
    return parsed && Date.now() - parsed.at < STATE_TTL_MS ? parsed : null;
  } catch {
    return null;
  }
};

const writeState = (state: StoredErrorState) => {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be blocked; the page still works, it just cannot remember a reload.
  }
};

const toError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  if (error && typeof error === "object" && "statusText" in error) {
    const response = error as { status?: number; statusText?: string };
    return new Error(`${response.status ?? ""} ${response.statusText ?? "Route error"}`.trim());
  }
  return new Error(typeof error === "string" ? error : "Unknown error");
};

const buildPayload = (error: Error, userReported: boolean) => ({
  message: error.message,
  errorName: error.name,
  stack: error.stack,
  url: window.location.href,
  userAgent: navigator.userAgent,
  recentActions: getActionTrail().map(({ id: _id, ...action }) => action),
  userReported,
});

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Records an application error on the server together with the user's last API calls.
 * Repeated renders of the same error (StrictMode, reload) reuse one log instead of creating more.
 */
export const captureError = (rawError: unknown): Promise<CapturedError> => {
  const error = toError(rawError);
  const signature = `${error.name}:${error.message}:${window.location.pathname}`;

  const existing = readState();
  if (existing?.signature === signature) return Promise.resolve({ id: existing.id, reloadedBefore: existing.reloaded });

  const pending = inFlight.get(signature);
  if (pending) return pending;

  const request = axios
    .post<{ id: string }>("/api/error-logs", buildPayload(error, false), {
      headers: authHeaders(),
      _skipAuthRefresh: true,
    } as object)
    .then(({ data }) => data.id)
    .catch((requestError) => {
      console.error("Failed to record error", requestError);
      return null;
    })
    .then((id) => {
      writeState({ signature, id, reloaded: false, at: Date.now() });
      return { id, reloadedBefore: false };
    })
    .finally(() => inFlight.delete(signature));

  inFlight.set(signature, request);
  return request;
};

/** Remembers that the user already tried reloading, so the next crash offers "Report error". */
export const markReloaded = () => {
  const state = readState();
  if (state) writeState({ ...state, reloaded: true, at: Date.now() });
};

/**
 * Flags the recorded error as explicitly reported by the user.
 * Falls back to creating an already-reported log when the first recording failed.
 * @returns True when the server accepted the report
 */
export const reportCapturedError = async (rawError: unknown, id: string | null): Promise<boolean> => {
  try {
    if (id) {
      await axios.post(`/api/error-logs/${id}/report`, {}, { headers: authHeaders(), _skipAuthRefresh: true } as object);
    } else {
      await axios.post("/api/error-logs", buildPayload(toError(rawError), true), {
        headers: authHeaders(),
        _skipAuthRefresh: true,
      } as object);
    }
    return true;
  } catch (requestError) {
    console.error("Failed to report error", requestError);
    return false;
  }
};
